// ─────────────────────────────────────────────────────────────────────────────
// Data-driven Movement System — JSON schema (TypeScript mirror)
//
// Estes tipos descrevem EXATAMENTE a forma dos arquivos JSON em
// `public/data/movement/`. Toda entidade móvel do jogo (NPCs, inimigos,
// pássaros) tem o comportamento definido por um destes perfis — nenhum código
// de gameplay precisa ser tocado para adicionar/ajustar movimento.
//
// Convenção de COMENTÁRIOS no JSON: JSON puro não aceita `//`. O loader
// (`MovementProfileLoader`) IGNORA qualquer chave que comece com `//` ou `_`,
// então você pode documentar os arquivos livremente com `"// nota": "..."`.
// ─────────────────────────────────────────────────────────────────────────────

/** Nomes das 8 direções, na ORDEM padrão das linhas do spritesheet (linha 0..7). */
export type Dir8 = 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'N' | 'NE' | 'E';

/** Ordem padrão: casa com o renderer de personagens já existente (getDirRow). */
export const DEFAULT_ROW_ORDER: Dir8[] = ['SE', 'S', 'SW', 'W', 'NW', 'N', 'NE', 'E'];

/**
 * Como converter o vetor de velocidade → linha do spritesheet.
 *  - `iso8`    : o vetor está em coordenadas de GRID; é convertido para a
 *               orientação de tela isométrica (e girado pelo ângulo da câmera)
 *               antes de escolher a direção. Use para NPCs andando no grid.
 *  - `screen8` : o vetor JÁ está em pixels de tela (y para baixo). 8 direções.
 *  - `screen4` : idem, mas colapsado em 4 direções (S, W, N, E).
 */
export type DirectionSet = 'iso8' | 'screen8' | 'screen4';

/** Bloco de animação — comum a NPC e (opcionalmente) pássaro em modo "sheet". */
export interface AnimationConfig {
  /** Nome do sprite sheet (sem extensão) — servido de `/sprites/<sheet>.png`. */
  sheet: string;
  /** Colunas do sheet (frames por linha). */
  cols: number;
  /** Linhas do sheet (1 por direção num sheet de 8 direções). */
  rows: number;
  /** Regra de mapeamento vetor→direção. Default: `iso8`. */
  directionSet?: DirectionSet;
  /** Coluna usada quando a entidade está PARADA. Default: 0. */
  idleCol?: number;
  /** Colunas que se alternam durante a caminhada. Default: `[1, 2]`. */
  walkCols?: number[];
  /** Velocidade da troca de frames de caminhada (frames/segundo). Default: 8. */
  walkFps?: number;
  /**
   * Ordem das linhas: `rowOrder[i]` é a direção representada pela linha `i` do
   * sheet. Default: `DEFAULT_ROW_ORDER`. Ajuste se o seu sheet usar outra
   * ordem (ex.: RPG Maker costuma ser ['S','W','E','N']).
   */
  rowOrder?: Dir8[];
  /** Direção de repouso quando a entidade nunca se moveu ainda. Default: 'S'. */
  restFacing?: Dir8;
  /**
   * Animações de AÇÃO (ex.: forjar, atacar, colher). Uma ação é tocada
   * enquanto a entidade ESPERA num waypoint que a referencia por nome
   * (`waypoint.action`). Ao terminar a espera, volta ao ciclo normal.
   */
  actions?: Record<string, ActionAnimation>;
}

/** Ciclo de frames de uma ação parada (forjar, atacar…). */
export interface ActionAnimation {
  /** Colunas do sheet cicladas durante a ação (ordem = ordem de exibição). */
  cols: number[];
  /** Velocidade do ciclo em frames/segundo. Default: 6. */
  fps?: number;
  /**
   * Direção que a entidade encara durante a ação. Default: mantém a última
   * direção de deslocamento. Afeta qual LINHA do sheet é usada.
   */
  facing?: Dir8;
  /** Linha fixa do sheet (sobrescreve a linha derivada de `facing`). */
  row?: number;
}

// ── NPCs / Patrulheiros ──────────────────────────────────────────────────────

export interface Waypoint {
  x: number;
  y: number;
  /** Segundos de espera AO CHEGAR neste ponto. Sobrescreve `waitTimeDefault`. */
  wait?: number;
  /**
   * Nome de uma ação em `animation.actions` a tocar durante a espera neste
   * ponto (ex.: `"forge"`). Só tem efeito se `wait > 0`.
   */
  action?: string;
}

export type LoopMode =
  | 'loop'      // ...→A→B→C→A→B→C→...
  | 'pingpong'  // ...→A→B→C→B→A→B→C→...
  | 'once';     // A→B→C e para no último

export type MoveSpace =
  | 'tile'    // waypoints em coordenadas de célula do grid; speed em tiles/s
  | 'screen'; // waypoints em pixels de tela; speed em px/s

export interface NpcStaticProfile {
  type: 'static';
  /** Direção fixa que o NPC encara. Default: 'S'. */
  facing?: Dir8;
  animation?: AnimationConfig;
}

export interface NpcPatrolProfile {
  type: 'patrol';
  loopMode?: LoopMode;              // default: 'loop'
  space?: MoveSpace;                // default: 'tile'
  /**
   * Se `true`, os `waypoints` são OFFSETS somados à `startPos` passada ao
   * registrar o NPC — em vez de coordenadas absolutas do mapa. Útil para
   * perfis reaproveitáveis ("forja no lugar", "ronda ao redor de casa") sem
   * fixar a posição no JSON. Ignorado se nenhuma `startPos` for informada.
   */
  relativeWaypoints?: boolean;
  /** Velocidade de deslocamento (tiles/s se space='tile', px/s se 'screen'). */
  speed: number;
  /** Espera padrão (s) em cada waypoint quando o waypoint não define `wait`. */
  waitTimeDefault?: number;         // default: 0
  /** Índice do waypoint onde a entidade começa. Default: 0. */
  startIndex?: number;
  /** Distância (na unidade de `space`) para considerar "chegou" ao waypoint. */
  arriveEpsilon?: number;           // default: 0.05 (tile) / 2 (screen)
  waypoints: Waypoint[];
  animation: AnimationConfig;
}

export type NpcProfile = NpcStaticProfile | NpcPatrolProfile;

export interface NpcBehaviorFile {
  version: number;
  profiles: Record<string, NpcProfile>;
}

// ── Pássaros / Vida selvagem ambiental ───────────────────────────────────────

/** De onde a entidade pode surgir. `random_offscreen` = ponto (X,Y) aleatório fora da tela. */
export type SpawnEdge = 'N' | 'S' | 'E' | 'W' | 'random_offscreen';

export interface BirdAltitudeConfig {
  /** Deslocamento vertical fixo (px) sorteado por pássaro, além da trajetória. */
  baseMinPx: number;
  baseMaxPx: number;
  /** Ondulação vertical senoidal. */
  bobAmplitudePx: number;
  bobPeriodSec: number;
}

export interface BirdCurveConfig {
  /** Desvio senoidal PERPENDICULAR à direção de voo (curvas suaves). */
  swayAmplitudePx: number;
  swayPeriodSec: number;
}

export interface BirdSpriteConfig {
  /** `procedural` = silhueta desenhada em código; `sheet` = usa `sheet`. */
  mode: 'procedural' | 'sheet';
  sheet?: string | null;
  cols?: number;
  rows?: number;
  /** Batida de asa (procedural) ou troca de frame (sheet), em fps. */
  flapFps?: number;
  scale?: number;
}

export interface BirdSpawnerConfig {
  enabled: boolean;
  /** Máximo de pássaros vivos ao mesmo tempo. */
  maxAlive: number;
  /** Intervalo (s) sorteado entre eventos de spawn. */
  spawnIntervalMin: number;
  spawnIntervalMax: number;
  /** Quantos pássaros nascem por evento de spawn. */
  spawnBurstMin: number;
  spawnBurstMax: number;
  /** Bordas permitidas para o surgimento. */
  edges: SpawnEdge[];
  /** Quão longe (px) fora da tela o pássaro nasce e é destruído (despawn). */
  edgeMarginPx: number;
  flight: {
    speedMinPxPerSec: number;
    speedMaxPxPerSec: number;
    /** Variação aleatória (graus) aplicada ao ângulo de voo base. */
    headingJitterDeg: number;
    altitude: BirdAltitudeConfig;
    curve: BirdCurveConfig;
  };
  sprite: BirdSpriteConfig;
  /** Águia caçadora (opcional): de vez em quando mergulha, agarra um pássaro e sai com ele. */
  eagle?: EagleConfig;
}

export interface EagleConfig {
  enabled: boolean;
  /** Intervalo (s) sorteado entre tentativas de caça. */
  intervalMinSec: number;
  intervalMaxSec: number;
  /** Chance (0..1) de a caça acontecer em cada tentativa (precisa haver pássaro no céu). */
  chance: number;
  /** Velocidade do mergulho (px/s); sai carregando a presa a ~70% disso. */
  speedPxPerSec: number;
  scale: number;
}

export interface AmbientBirdsFile {
  version: number;
  spawners: Record<string, BirdSpawnerConfig>;
}

// ── Animação de caminhada de PERSONAGENS / MONSTROS (combate + hub) ──────────
//
// Diferente dos perfis de NPC ambiental: aqui as entidades são movidas pelo
// SERVIDOR (combate) ou pelo clique do jogador (hub). Este bloco só ajusta a
// APARÊNCIA do deslocamento — passo casa-a-casa, ritmo, pulinho — via JSON, sem
// recompilar. Arquivo: `public/data/movement/character_movement.json`.

export type EasingName = 'smoothstep' | 'linear';

export interface CharacterMovementConfig {
  version?: number;
  /** ms por passo (uma célula → célula adjacente). Total = stepMs × nº de células. */
  stepMs: number;
  /** curva de interpolação dentro de cada passo. */
  easing: EasingName;
  /** altura (px) do "pulinho" de passada, um arco por célula. 0 = sem pulo. */
  bobHeightPx: number;
  /** ms por frame da folha de sprite enquanto anda (ciclo das colunas de passo). */
  walkFrameMs: number;
  /** Espaçamento da cascata de inimigos (andam um de cada vez, não juntos). */
  enemyCascade: {
    /** pausa (ms) depois do jogador terminar de andar, antes do 1º inimigo. */
    afterPlayerMs: number;
    /** pausa (ms) entre um inimigo e o próximo. */
    betweenEnemiesMs: number;
  };
  /**
   * Reconstruir o caminho no cliente (BFS 4-direções, igual ao MovementEngine
   * do backend) quando o servidor não enviar `movements` — ex.: clique longe
   * no Hub. `false` = desliza em linha reta nesse caso.
   */
  clientPathfindingFallback: boolean;
}

// ── Viewport passado ao sistema a cada frame ─────────────────────────────────

export interface Viewport {
  /** Largura/altura visíveis em px (na MESMA escala em que os pássaros vivem). */
  width: number;
  height: number;
  /** Canto superior-esquerdo visível (para mundos que rolam). Default: 0,0. */
  offsetX?: number;
  offsetY?: number;
}
